import { CurrentState, OfflineUpdateSummary } from "./OfflineUpdateSummary";
import { buildUrlListForAllPagesOfAllSongs } from "./BuildUrlListForAllPagesOfAllSongs";
import { SongUb } from "../gql/graphql";

export class Blackboard {
  private readonly notifyProgress: (
    value:
      | ((prevState: OfflineUpdateSummary) => OfflineUpdateSummary)
      | OfflineUpdateSummary,
  ) => void;

  constructor(
    notifyProgress: (
      value:
        | ((prevState: OfflineUpdateSummary) => OfflineUpdateSummary)
        | OfflineUpdateSummary,
    ) => void,
    songs: readonly SongUb[],
  ) {
    this.notifyProgress = notifyProgress;
    this.setUrlsFromToC(buildUrlListForAllPagesOfAllSongs(songs));
  }

  private lastNotifiedDownloadTimestamp = new Date();

  failures: Array<string> = [];
  private _input = {
    urlsFromToc: new Set<string>(),
    urlsFromCache: new Set<string>(),
  };
  private _work = {
    urlsToBeDownloaded: new Array<string>(),
    urlsToBeDeleted: new Array<string>(),
  };

  get input(): { urlsFromToc: Set<string>; urlsFromCache: Set<string> } {
    return this._input;
  }

  private setUrlsFromToC(value: Set<string>) {
    this._input.urlsFromToc = value;
    this.notifyProgress((v) => ({ ...v, amountInToc: value.size }));
  }

  public setUrlsFromCache(value: Set<string>) {
    this._input.urlsFromCache = value;
    this.notifyProgress((v) => ({ ...v, amountInCache: value.size }));
  }

  get work(): { urlsToBeDownloaded: string[]; urlsToBeDeleted: string[] } {
    return this._work;
  }

  set work(work: { urlsToBeDownloaded: string[]; urlsToBeDeleted: string[] }) {
    this._work = work;
    this.notifyProgress((v) => ({
      ...v,
      initialAmountToBeDownloaded: work.urlsToBeDownloaded.length,
      leftAmountToBeDownloaded: work.urlsToBeDownloaded.length,
      initialAmountToBeDeleted: work.urlsToBeDeleted.length,
    }));
  }

  set toBeDownloaded(value: string[]) {
    this._work.urlsToBeDownloaded = value;
    this.notifyProgress((v) => ({
      ...v,
      initialAmountToBeDownloaded: value.length,
      leftAmountToBeDownloaded: value.length,
    }));
  }

  set toBeDeleted(value: string[]) {
    this._work.urlsToBeDeleted = value;
    this.notifyProgress((v) => ({
      ...v,
      initialAmountToBeDeleted: value.length,
    }));
  }

  public updateAndNotifyCurrentState(state: CurrentState) {
    this.notifyProgress((v) => ({ ...v, currentState: state }));
  }

  public updateDownloadsLeft(leftAmountToBeDownloaded: number) {
    const diff =
      new Date().getTime() - this.lastNotifiedDownloadTimestamp.getTime();
    if (diff > 1000) {
      this.lastNotifiedDownloadTimestamp = new Date();
      this.notifyProgress((v) => ({
        ...v,
        currentState: CurrentState.DOWNLOAD,
        leftAmountToBeDownloaded: leftAmountToBeDownloaded,
      }));
    }
  }
}
